import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { parseUtc } from '../lib/utils'
import {
  RefreshCw, AlertTriangle, CheckCircle2, CheckCheck, Clock, XCircle, Radio,
  Search, ArrowUp, ArrowDown, ArrowUpDown, X, Eye, MessageSquare,
} from 'lucide-react'

// Messaging Log — one page, two channels (SMS + WhatsApp), toggled at the
// top. Both read from local audit tables written on every send:
//   SMS      -> sms_log       (lib/twilio.ts)        /admin/sms-log
//   WhatsApp -> whatsapp_log  (lib/whatsapp.ts)      /admin/whatsapp-log
// Rows are normalised into a single MsgRow view model so the table, KPIs,
// abuse banner, and filters are shared; only the channel-specific bits
// (cost vs pricing category, Twilio SID vs wamid, Twilio balance widget)
// branch.
//
// Bodies are stored with OTP codes redacted at write time (******) so no
// admin viewing the log can shoulder-surf an active verification code
// paired with a recipient's number.

type Channel = 'sms' | 'whatsapp'

// ---- Raw API shapes ----
interface SmsLogRaw {
  id: string
  to_phone: string
  to_phone_masked: string
  from_sender: string
  body_snippet: string
  source: string
  source_ref_id: string | null
  twilio_sid: string | null
  twilio_status: string
  error_code: number | null
  error_message: string | null
  price_micro_usd: number | null
  created_at: string
}
interface SmsSummaryRaw {
  total: number
  spend_micro_usd: number
  delivered: number
  failed_or_undelivered: number
  with_errors: number
  by_source: { source: string; count: number; spend_micro_usd: number }[]
  top_phones: { to_phone_masked: string; count: number }[]
}
interface SmsResponse { window_hours: number; messages: SmsLogRaw[]; summary: SmsSummaryRaw }

interface WaLogRaw {
  id: string
  to_phone: string
  to_phone_masked: string
  from_number: string
  template: string
  template_lang: string | null
  body_snippet: string
  source: string
  source_ref_id: string | null
  wamid: string | null
  status: string
  error_code: number | null
  error_message: string | null
  conversation_id: string | null
  pricing_category: string | null
  pricing_billable: number | null
  created_at: string
  updated_at: string | null
  sms_resent_at: string | null
}
interface WaSummaryRaw {
  total: number
  billable: number
  delivered: number
  read: number
  failed: number
  with_errors: number
  by_source: { source: string; count: number; billable: number }[]
  top_phones: { to_phone_masked: string; count: number }[]
}
interface WaResponse { window_hours: number; messages: WaLogRaw[]; summary: WaSummaryRaw }

// ---- Normalised view model ----
interface MsgRow {
  id: string
  to_phone: string
  source: string
  source_ref_id: string | null
  status: string
  snippet: string
  created_at: string
  error_code: number | null
  error_message: string | null
  cost: string            // SMS: "$0.0123" · WhatsApp: "authentication"
  billable?: boolean      // WhatsApp only — drives the billable dot
  template?: string       // WhatsApp only
  recovered?: boolean     // WhatsApp only — failed but SMS fallback sent
}
interface NormSummary {
  total: number
  delivered: number
  failed: number
  with_errors: number
  spendLabel: string
  spendSub: string
  topSourceSub: string
  by_source: { source: string; count: number; costLabel: string }[]
  top_phones: { to_phone_masked: string; count: number }[]
}

interface TwilioAccount { friendly_name: string; status: string; type: string }
interface TwilioStatus { account?: TwilioAccount; balance?: string }

const STATUS_META: Record<string, { chip: string; icon: React.ReactNode; label: string }> = {
  accepted:    { chip: 'bg-blue-500/15 text-blue-400',    icon: <Clock size={12} />,        label: 'Accepted' },
  delivered:   { chip: 'bg-green-500/15 text-green-400',  icon: <CheckCircle2 size={12} />, label: 'Delivered' },
  read:        { chip: 'bg-green-500/20 text-green-300',  icon: <CheckCheck size={12} />,   label: 'Read' },
  sent:        { chip: 'bg-amber-500/15 text-amber-400',  icon: <Clock size={12} />,        label: 'Sent' },
  queued:      { chip: 'bg-blue-500/15 text-blue-400',    icon: <Clock size={12} />,        label: 'Queued' },
  sending:     { chip: 'bg-blue-500/15 text-blue-400',    icon: <Clock size={12} />,        label: 'Sending' },
  undelivered: { chip: 'bg-red-500/15 text-red-400',      icon: <XCircle size={12} />,      label: 'Undelivered' },
  failed:      { chip: 'bg-red-500/15 text-red-400',      icon: <XCircle size={12} />,      label: 'Failed' },
}

const STATUS_OPTIONS: Record<Channel, Array<{ value: string; label: string }>> = {
  sms: [
    { value: 'delivered', label: 'Delivered' },
    { value: 'sent', label: 'Sent' },
    { value: 'queued', label: 'Queued' },
    { value: 'failed', label: 'Failed' },
    { value: 'undelivered', label: 'Undelivered' },
  ],
  whatsapp: [
    { value: 'accepted', label: 'Accepted' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'read', label: 'Read' },
    { value: 'failed', label: 'Failed' },
  ],
}

const WINDOW_OPTIONS: Array<{ label: string; hours: number }> = [
  { label: '24h',  hours: 24 },
  { label: '7d',   hours: 24 * 7 },
  { label: '30d',  hours: 24 * 30 },
]

const ABUSE_THRESHOLD_PER_PHONE = 8

type SortKey = 'created' | 'source' | 'to' | 'status'
type SortDir = 'asc' | 'desc'

function fmtTime(iso: string): string {
  return parseUtc(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Amman' })
}
function fmtCost(micro: number | null | undefined): string {
  if (micro == null || micro === 0) return '—'
  return `$${(micro / 1_000_000).toFixed(4)}`
}
function fmtCostSummed(micro: number): string {
  if (micro <= 0) return '$0.00'
  return `$${(micro / 1_000_000).toFixed(2)}`
}

// ---- Normalisers ----
function normSms(res: SmsResponse): { rows: MsgRow[]; summary: NormSummary } {
  const rows: MsgRow[] = res.messages.map(m => ({
    id: m.id,
    to_phone: m.to_phone,
    source: m.source,
    source_ref_id: m.source_ref_id,
    status: m.twilio_status,
    snippet: m.body_snippet,
    created_at: m.created_at,
    error_code: m.error_code,
    error_message: m.error_message,
    cost: fmtCost(m.price_micro_usd),
  }))
  const s = res.summary
  return {
    rows,
    summary: {
      total: s.total,
      delivered: s.delivered,
      failed: s.failed_or_undelivered,
      with_errors: s.with_errors,
      spendLabel: fmtCostSummed(s.spend_micro_usd),
      spendSub: 'Twilio USD',
      topSourceSub: s.by_source[0] ? `${s.by_source[0].count} sent` : '',
      by_source: s.by_source.map(x => ({ source: x.source, count: x.count, costLabel: fmtCostSummed(x.spend_micro_usd) })),
      top_phones: s.top_phones,
    },
  }
}
function normWa(res: WaResponse): { rows: MsgRow[]; summary: NormSummary } {
  const rows: MsgRow[] = res.messages.map(m => ({
    id: m.id,
    to_phone: m.to_phone,
    source: m.source,
    source_ref_id: m.source_ref_id,
    status: m.status,
    snippet: m.body_snippet,
    created_at: m.created_at,
    error_code: m.error_code,
    error_message: m.error_message,
    cost: m.pricing_category ?? '—',
    billable: m.pricing_billable === 1,
    template: m.template,
    recovered: m.status === 'failed' && !!m.sms_resent_at,
  }))
  const s = res.summary
  return {
    rows,
    summary: {
      total: s.total,
      delivered: s.delivered,
      failed: s.failed,
      with_errors: s.with_errors,
      spendLabel: String(s.billable),
      spendSub: 'billable convos',
      topSourceSub: s.by_source[0] ? `${s.by_source[0].count} sent` : '',
      by_source: s.by_source.map(x => ({ source: x.source, count: x.count, costLabel: `${x.billable} billable` })),
      top_phones: s.top_phones,
    },
  }
}

export default function SmsLog() {
  // Open on the WhatsApp tab when linked with ?channel=whatsapp (e.g. from
  // the Dashboard WhatsApp strip); default to SMS otherwise.
  const [channel, setChannel] = useState<Channel>(() => {
    try {
      return new URLSearchParams(window.location.search).get('channel') === 'whatsapp'
        ? 'whatsapp' : 'sms'
    } catch { return 'sms' }
  })
  const [rows, setRows] = useState<MsgRow[]>([])
  const [summary, setSummary] = useState<NormSummary | null>(null)
  const [twilio, setTwilio] = useState<TwilioStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hours, setHours] = useState<number>(24)
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [hasErrorOnly, setHasErrorOnly] = useState<boolean>(false)
  // WhatsApp only: hide failed rows that were recovered by the SMS fallback.
  const [hideRecovered, setHideRecovered] = useState<boolean>(false)
  const [sortKey, setSortKey] = useState<SortKey>('created')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [detail, setDetail] = useState<{ channel: Channel; sms?: SmsLogRaw & { body: string }; wa?: WaLogRaw & { body: string } } | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('hours', String(hours))
      if (sourceFilter)  params.set('source', sourceFilter)
      if (statusFilter)  params.set('status', statusFilter)
      if (hasErrorOnly)  params.set('has_error', 'true')
      if (search.trim()) params.set('phone', search.trim())

      if (channel === 'sms') {
        const [logRes, twStatusRes] = await Promise.all([
          api.get<SmsResponse>(`/sms-log?${params.toString()}`),
          api.get<TwilioStatus>('/twilio-status?page_size=1').catch(() => ({ data: {} as TwilioStatus })),
        ])
        const n = normSms(logRes.data)
        setRows(n.rows); setSummary(n.summary); setTwilio(twStatusRes.data)
      } else {
        const logRes = await api.get<WaResponse>(`/whatsapp-log?${params.toString()}`)
        const n = normWa(logRes.data)
        setRows(n.rows); setSummary(n.summary); setTwilio(null)
      }
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  // Reset status filter when switching channels (vocabularies differ).
  useEffect(() => { setStatusFilter('') }, [channel])
  useEffect(() => { load() }, [channel, hours, sourceFilter, statusFilter, hasErrorOnly])

  const abusePhones = (summary?.top_phones ?? []).filter(p => p.count >= ABUSE_THRESHOLD_PER_PHONE)
  const windowLabel = WINDOW_OPTIONS.find(w => w.hours === hours)?.label ?? `${hours}h`
  const isWa = channel === 'whatsapp'
  const noun = isWa ? 'WhatsApp' : 'SMS'

  const visible = useMemo(() => {
    const base = (channel === 'whatsapp' && hideRecovered)
      ? rows.filter(r => !r.recovered)
      : rows
    const dir = sortDir === 'asc' ? 1 : -1
    return [...base].sort((a, b) => {
      switch (sortKey) {
        case 'created': return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir
        case 'source':  return a.source.localeCompare(b.source) * dir
        case 'to':      return a.to_phone.localeCompare(b.to_phone) * dir
        case 'status':  return a.status.localeCompare(b.status) * dir
      }
    })
  }, [rows, sortKey, sortDir, channel, hideRecovered])

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir(key === 'created' ? 'desc' : 'asc') }
  }

  async function openDetail(id: string) {
    setDetailLoading(true)
    try {
      if (channel === 'sms') {
        const res = await api.get<SmsLogRaw & { body: string }>(`/sms-log/${id}`)
        setDetail({ channel: 'sms', sms: res.data })
      } else {
        const res = await api.get<WaLogRaw & { body: string }>(`/whatsapp-log/${id}`)
        setDetail({ channel: 'whatsapp', wa: res.data })
      }
    } catch (e) {
      setDetail(null)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setDetailLoading(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Radio size={24} className="text-[#C41E3A]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Messaging Log</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Every {noun} we send, by source. Bodies are stored with OTP codes redacted.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Channel toggle */}
          <div className="flex items-center gap-1 rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] p-1">
            <button
              onClick={() => setChannel('sms')}
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                channel === 'sms' ? 'bg-[#C41E3A]/15 text-[#C41E3A]' : 'text-gray-400 hover:text-white'
              }`}
            ><Radio size={13} /> SMS</button>
            <button
              onClick={() => setChannel('whatsapp')}
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                channel === 'whatsapp' ? 'bg-[#25D366]/15 text-[#25D366]' : 'text-gray-400 hover:text-white'
              }`}
            ><MessageSquare size={13} /> WhatsApp</button>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] p-1">
            {WINDOW_OPTIONS.map(w => (
              <button
                key={w.hours}
                onClick={() => setHours(w.hours)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  hours === w.hours ? 'bg-[#C41E3A]/15 text-[#C41E3A]' : 'text-gray-400 hover:text-white'
                }`}
              >{w.label}</button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] px-3 py-2 text-sm font-medium text-white hover:border-[#C41E3A]/50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          <AlertTriangle size={16} className="flex-none mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {abusePhones.length > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          <AlertTriangle size={16} className="flex-none mt-0.5 text-amber-400" />
          <div className="flex-1">
            <div className="font-semibold text-amber-100">
              {abusePhones.length} phone number{abusePhones.length === 1 ? '' : 's'} received {ABUSE_THRESHOLD_PER_PHONE}+ {noun} in the last {windowLabel}
            </div>
            <div className="mt-1 text-amber-200/80 font-mono text-xs">
              {abusePhones.map(p => `${p.to_phone_masked} (${p.count})`).join('  ·  ')}
            </div>
            <div className="mt-2 text-amber-200/70">
              Legitimate high-activity customers can trigger this; filter by phone to see the source mix and decide whether to investigate.
            </div>
          </div>
        </div>
      )}

      {summary && (
        <div className={`grid grid-cols-2 ${isWa ? 'lg:grid-cols-4' : 'lg:grid-cols-5'} gap-3 mb-6`}>
          <Kpi label="Total sent" value={String(summary.total)} sublabel={`in ${windowLabel}`} />
          <Kpi label={isWa ? 'Billable' : 'Spend'} value={summary.spendLabel} sublabel={summary.spendSub} />
          <Kpi
            label="Failed"
            value={String(summary.failed)}
            sublabel={`vs ${summary.delivered} delivered`}
            highlight={summary.failed > 0 ? 'red' : undefined}
          />
          {!isWa && (
            <Kpi label="Twilio balance" value={twilio?.balance ?? '—'} sublabel={twilio?.account?.status ?? ''} />
          )}
          <Kpi
            label="Top source"
            value={summary.by_source[0]?.source ?? '—'}
            sublabel={summary.topSourceSub}
          />
        </div>
      )}

      {summary && summary.by_source.length > 0 && (
        <div className="mb-4 overflow-hidden rounded-xl border border-[#2e2e2e] bg-[#1a1a1a]">
          <div className="flex items-center justify-between border-b border-[#2e2e2e] px-4 py-2.5">
            <div className="text-xs uppercase tracking-wider text-gray-500">By source</div>
            {sourceFilter && (
              <button onClick={() => setSourceFilter('')} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
                <X size={12} /> Clear filter
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 p-3">
            {summary.by_source.map(s => {
              const active = sourceFilter === s.source
              return (
                <button
                  key={s.source}
                  onClick={() => setSourceFilter(active ? '' : s.source)}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-[#C41E3A] bg-[#C41E3A]/15 text-[#C41E3A]'
                      : 'border-[#2e2e2e] bg-[#0f0f0f] text-gray-300 hover:border-[#C41E3A]/40 hover:text-white'
                  }`}
                >
                  <span>{s.source}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${active ? 'bg-[#C41E3A]/20' : 'bg-white/[0.06] text-gray-400'}`}>
                    {s.count}
                  </span>
                  <span className="text-[10px] text-gray-500">{s.costLabel}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] px-2.5 py-1.5 text-xs text-white focus:border-[#C41E3A]/60 focus:outline-none"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS[channel].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={hasErrorOnly}
              onChange={e => setHasErrorOnly(e.target.checked)}
              className="rounded border-[#2e2e2e] bg-[#1a1a1a] text-[#C41E3A] focus:ring-[#C41E3A]/60"
            />
            Errors only
          </label>
          {isWa && (
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={hideRecovered}
                onChange={e => setHideRecovered(e.target.checked)}
                className="rounded border-[#2e2e2e] bg-[#1a1a1a] text-[#C41E3A] focus:ring-[#C41E3A]/60"
              />
              Hide recovered
            </label>
          )}
        </div>
        <div className="relative flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Filter by phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load() }}
              className="w-full md:w-64 pl-8 pr-8 py-1.5 text-sm rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] text-white placeholder:text-gray-600 focus:border-[#C41E3A]/60 focus:outline-none"
            />
            {search && (
              <button onClick={() => { setSearch(''); load() }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
          <button onClick={load} className="rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] px-2.5 py-1.5 text-xs text-gray-400 hover:text-white">Apply</button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#2e2e2e] bg-[#1a1a1a]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-gray-500">
              <tr>
                <SortHeader label="Status" active={sortKey === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
                <SortHeader label="Source" active={sortKey === 'source'} dir={sortDir} onClick={() => toggleSort('source')} />
                <SortHeader label="To"     active={sortKey === 'to'}     dir={sortDir} onClick={() => toggleSort('to')} />
                <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider">{isWa ? 'Template / Snippet' : 'Snippet'}</th>
                <SortHeader label="Created" active={sortKey === 'created'} dir={sortDir} onClick={() => toggleSort('created')} />
                <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider">{isWa ? 'Pricing' : 'Cost'}</th>
                <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider">Error</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2e2e2e]">
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">
                    {rows.length === 0 ? `No ${noun} in this window.` : `No ${noun} match the current filters.`}
                  </td>
                </tr>
              )}
              {visible.map(m => {
                const meta = STATUS_META[m.status] ?? { chip: 'bg-gray-500/15 text-gray-400', icon: <Clock size={12} />, label: m.status }
                return (
                  <tr key={m.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}>
                        {meta.icon}{meta.label}
                      </span>
                      {m.recovered && (
                        <span
                          className="ml-1 inline-flex items-center gap-1 rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] font-medium text-blue-300"
                          title="WhatsApp failed; the SMS fallback was sent"
                        >
                          → SMS sent
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block rounded bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-mono text-gray-300">{m.source}</span>
                      {m.source_ref_id && (
                        <div className="mt-1 text-[10px] text-gray-600 font-mono truncate max-w-[140px]">{m.source_ref_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-white">{m.to_phone}</td>
                    <td className="px-4 py-3 text-xs text-gray-300 max-w-md">
                      {isWa && m.template && (
                        <div className="mb-0.5 inline-block rounded bg-[#25D366]/10 px-1.5 py-0.5 text-[10px] font-mono text-[#25D366]">{m.template}</div>
                      )}
                      <div className="truncate">{m.snippet}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtTime(m.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap font-mono">
                      {isWa
                        ? (m.cost === '—' ? <span className="text-gray-600">—</span>
                            : <span className="inline-flex items-center gap-1">{m.cost}{m.billable && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" title="billable" />}</span>)
                        : m.cost}
                    </td>
                    <td className="px-4 py-3 text-xs text-red-400">
                      {m.error_code ? `${m.error_code}: ${(m.error_message ?? '').slice(0, 40)}` : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-2 py-3">
                      <button onClick={() => openDetail(m.id)} className="rounded p-1 text-gray-500 hover:bg-white/[0.06] hover:text-white" aria-label="View full body">
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-600">
        Showing {visible.length} of {rows.length} {noun} in the last {windowLabel}. Bodies are stored with OTP codes redacted (******).
      </p>

      {detail && <DetailModal detail={detail} onClose={() => setDetail(null)} />}
      {detailLoading && !detail && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] px-4 py-3 text-sm text-gray-300">Loading…</div>
        </div>
      )}
    </div>
  )
}

function SortHeader({ label, active, dir, onClick }: {
  label: string; active: boolean; dir: SortDir; onClick: () => void
}) {
  return (
    <th className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider">
      <button onClick={onClick} className={`inline-flex items-center gap-1 transition-colors ${active ? 'text-white' : 'text-gray-500 hover:text-white'}`}>
        {label}
        {active ? (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />}
      </button>
    </th>
  )
}

function Kpi({ label, value, sublabel, highlight }: {
  label: string; value: string; sublabel?: string; highlight?: 'amber' | 'red'
}) {
  const borderClass = highlight === 'amber' ? 'border-amber-500/40' : highlight === 'red' ? 'border-red-500/40' : 'border-[#2e2e2e]'
  const valueClass = highlight === 'amber' ? 'text-amber-400' : highlight === 'red' ? 'text-red-400' : 'text-white'
  return (
    <div className={`bg-[#1a1a1a] border ${borderClass} rounded-xl px-4 py-3`}>
      <p className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-lg font-semibold mt-0.5 ${valueClass} truncate`}>{value}</p>
      {sublabel && <p className="text-[11px] text-gray-600 mt-0.5 truncate">{sublabel}</p>}
    </div>
  )
}

function DetailModal({ detail, onClose }: {
  detail: { channel: Channel; sms?: SmsLogRaw & { body: string }; wa?: WaLogRaw & { body: string } }
  onClose: () => void
}) {
  const isWa = detail.channel === 'whatsapp'
  const status = isWa ? detail.wa!.status : detail.sms!.twilio_status
  const source = isWa ? detail.wa!.source : detail.sms!.source
  const meta = STATUS_META[status] ?? { chip: 'bg-gray-500/15 text-gray-400', icon: <Clock size={12} />, label: status }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl border border-[#2e2e2e] bg-[#141414] shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[#2e2e2e] px-5 py-3">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}>
              {meta.icon}{meta.label}
            </span>
            <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[11px] font-mono text-gray-300">{source}</span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isWa ? 'bg-[#25D366]/15 text-[#25D366]' : 'bg-blue-500/15 text-blue-400'}`}>
              {isWa ? 'WhatsApp' : 'SMS'}
            </span>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-white/[0.06] hover:text-white"><X size={16} /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {isWa ? (
            <>
              <MetaRow label="Sent at"    value={fmtTime(detail.wa!.created_at)} />
              <MetaRow label="Updated"    value={detail.wa!.updated_at ? fmtTime(detail.wa!.updated_at) : '—'} />
              <MetaRow label="To"         value={detail.wa!.to_phone} mono />
              <MetaRow label="From"       value={detail.wa!.from_number} mono />
              <MetaRow label="Template"   value={`${detail.wa!.template}${detail.wa!.template_lang ? ` (${detail.wa!.template_lang})` : ''}`} mono />
              <MetaRow label="wamid"      value={detail.wa!.wamid ?? '—'} mono />
              <MetaRow label="Conv. id"   value={detail.wa!.conversation_id ?? '—'} mono />
              <MetaRow label="Pricing"    value={detail.wa!.pricing_category ? `${detail.wa!.pricing_category}${detail.wa!.pricing_billable === 1 ? ' · billable' : ' · free'}` : '—'} />
              <MetaRow label="Ref ID"     value={detail.wa!.source_ref_id ?? '—'} mono />
              {detail.wa!.error_code && (
                <MetaRow label="Error" value={`${detail.wa!.error_code}: ${detail.wa!.error_message ?? ''}`} highlight="red" />
              )}
              {detail.wa!.sms_resent_at && (
                <MetaRow label="SMS sent" value={`Fallback SMS sent ${fmtTime(detail.wa!.sms_resent_at)}`} />
              )}
            </>
          ) : (
            <>
              <MetaRow label="Sent at"    value={fmtTime(detail.sms!.created_at)} />
              <MetaRow label="To"         value={detail.sms!.to_phone} mono />
              <MetaRow label="From"       value={detail.sms!.from_sender} mono />
              <MetaRow label="Twilio SID" value={detail.sms!.twilio_sid ?? '—'} mono />
              <MetaRow label="Ref ID"     value={detail.sms!.source_ref_id ?? '—'} mono />
              <MetaRow label="Cost"       value={fmtCost(detail.sms!.price_micro_usd)} />
              {detail.sms!.error_code && (
                <MetaRow label="Error" value={`${detail.sms!.error_code}: ${detail.sms!.error_message ?? ''}`} highlight="red" />
              )}
            </>
          )}
          <div>
            <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Body (redacted)</div>
            <pre className="whitespace-pre-wrap rounded-lg border border-[#2e2e2e] bg-[#0f0f0f] p-3 text-sm text-gray-100 font-mono">{isWa ? detail.wa!.body : detail.sms!.body}</pre>
            <p className="mt-1 text-[10px] text-gray-600">
              OTP codes replaced with ****** at store time. {isWa ? 'Template params' : 'Order numbers, URLs, and prices'} are preserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function MetaRow({ label, value, mono, highlight }: { label: string; value: string; mono?: boolean; highlight?: 'red' }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-24 flex-none text-[11px] text-gray-500 uppercase tracking-wider pt-0.5">{label}</div>
      <div className={`flex-1 text-sm ${mono ? 'font-mono' : ''} ${highlight === 'red' ? 'text-red-400' : 'text-gray-100'} break-all`}>{value}</div>
    </div>
  )
}
